/* ═══════════════════════════════════════════════════════════════
   SmartBlockBuilder — the three-rung authoring surface that lives
   inside the Smart Blocks editor.

     • Quick    — tweak a recipe: identity + default values, no logic.
     • Build    — visual no-code composer (source → filters → order →
                  layout → inputs) that GENERATES querySpec + template.
     • Advanced — the raw query + Handlebars template editor for power
                  users (the door the future scripting vision enters).

   Non-developers (architects/engineers) live in Quick + Build. Nothing
   here should require typing a field name or a template token. The
   engine (engine/smart-blocks/resolvers.ts) is already capable — this
   is purely the authoring UX on top of it.
   ═══════════════════════════════════════════════════════════════ */

import React, { useMemo, useState, useRef, useEffect } from 'react';
import * as LucideIcons from 'lucide-react';
import {
  Wand2,
  SlidersHorizontal,
  Code2,
  Plus,
  Trash2,
  X,
  Search,
  Check,
  Eye,
  CornerDownRight,
} from 'lucide-react';
import type {
  SmartBlockDraft,
  SmartBlockParam,
  SmartBlockParamType,
  SmartBlockQuerySpec,
  SmartBlockQueryClause,
  SmartBlock,
  ContentBlock,
  TableData,
  ProjectContext,
} from '../../../shared/types';
import { resolveSmartToken, builtInIdFor } from '../../../engine/smart-blocks';
import { SearchableSelect } from '../SearchableSelect';

export type RungId = 'quick' | 'build' | 'advanced';
type QuerySource = SmartBlockQuerySpec['from'];
type LayoutKind = NonNullable<SmartBlockQuerySpec['layout']>;

interface BodyProps {
  draft: SmartBlockDraft;
  update: <K extends keyof SmartBlockDraft>(key: K, val: SmartBlockDraft[K]) => void;
  /** Multi-field atomic update — used by the composer to set query + template
   *  + params together so a single edit produces one coherent draft state. */
  patch: (partial: Partial<SmartBlockDraft>) => void;
}

/* ─── Friendly vocabulary (maps internal model → plain English) ───────── */

const SOURCES: { value: QuerySource; label: string; hint: string }[] = [
  {
    value: 'project-specs',
    label: 'Other sections in this project',
    hint: 'Every other spec section in the project',
  },
  { value: 'team-members', label: 'Project team', hint: 'People assigned to the project' },
  {
    value: 'project-fields',
    label: "This project's info",
    hint: 'Project name, number, address, phase',
  },
  { value: 'issuances', label: 'Current issuance', hint: 'The active issuance (IFC, Addendum…)' },
  { value: 'specs-in-section', label: 'Text in this section', hint: "Scan this spec's own lines" },
];

const FIELDS_BY_SOURCE: Record<QuerySource, { value: string; label: string }[]> = {
  'project-specs': [
    { value: 'csiNumber', label: 'CSI number' },
    { value: 'title', label: 'Title' },
    { value: 'division', label: 'Division' },
    { value: 'sectionNumber', label: 'Section number' },
  ],
  'team-members': [
    { value: 'name', label: 'Name' },
    { value: 'title', label: 'Title' },
    { value: 'role', label: 'Role' },
    { value: 'email', label: 'Email' },
  ],
  'project-fields': [
    { value: 'name', label: 'Project name' },
    { value: 'number', label: 'Project number' },
    { value: 'address', label: 'Address' },
    { value: 'phase', label: 'Phase' },
  ],
  issuances: [
    { value: 'name', label: 'Issuance name' },
    { value: 'date', label: 'Date' },
  ],
  'specs-in-section': [
    { value: 'text', label: 'Line text' },
    { value: 'level', label: 'Outline level' },
  ],
};

// Build rung keeps the four operators an architect actually reaches for.
// The full 7-op DSL (in / not-in / regex) stays available in Advanced.
const OPERATORS: { value: SmartBlockQueryClause['op']; label: string }[] = [
  { value: 'equals', label: 'is' },
  { value: 'not-equals', label: 'is not' },
  { value: 'contains', label: 'contains' },
  { value: 'starts-with', label: 'starts with' },
];

const LAYOUTS: { value: LayoutKind; label: string; hint: string; soon?: boolean }[] = [
  { value: 'bulleted', label: 'Bulleted list', hint: 'One item per line' },
  { value: 'labeled', label: 'Labeled lines', hint: 'Label: value, one per line' },
  { value: 'inline', label: 'Inline list', hint: 'Comma-separated, one paragraph' },
  { value: 'table', label: 'Table', hint: 'Columns — a real Word table' },
];

const PARAM_TYPES: { value: SmartBlockParamType; label: string }[] = [
  { value: 'string', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'boolean', label: 'Yes / no' },
  { value: 'enum', label: 'Choice' },
  { value: 'multiselect', label: 'Multi-choice' },
  { value: 'division', label: 'CSI division' },
  { value: 'spec-ref-list', label: 'Pick sections' },
];

const ICON_CHOICES = [
  'Sparkles',
  'Link2',
  'BookOpen',
  'Users',
  'Hash',
  'Calendar',
  'FileText',
  'List',
  'ListChecks',
  'Tag',
  'Tags',
  'MapPin',
  'Building2',
  'Briefcase',
  'Award',
  'Stamp',
  'PenTool',
  'Ruler',
  'Layers',
  'Boxes',
  'ClipboardList',
  'Quote',
  'Library',
  'Network',
  'GitBranch',
  'Workflow',
  'Table',
  'AlignLeft',
  'Type',
  'Anchor',
  'Compass',
  'Shield',
  'BadgeCheck',
  'Star',
  'Bookmark',
  'Folder',
  'Pin',
  'Send',
  'Zap',
  'Hammer',
];

const COLOR_CHOICES = [
  '#1B8AAD',
  '#228be6',
  '#7950f2',
  '#9c36b5',
  '#e64980',
  '#e8590c',
  '#f08c00',
  '#2f9e44',
  '#495057',
];

/* ─── Template generation (Build composer → Handlebars-lite) ───────────── */

function defaultFieldsFor(from: QuerySource): string[] {
  switch (from) {
    case 'project-specs':
      return ['csiNumber', 'title'];
    case 'team-members':
      return ['name', 'title'];
    case 'project-fields':
      return ['name', 'number'];
    case 'issuances':
      return ['name', 'date'];
    case 'specs-in-section':
      return ['text'];
  }
}

function labelForField(from: QuerySource, field: string): string {
  return FIELDS_BY_SOURCE[from]?.find((f) => f.value === field)?.label ?? field;
}

/** Turn a composer querySpec into a working template string. Uses only
 *  engine-supported syntax: {{#each}}, {{#if @index}} for clean separators
 *  (no trailing comma), and plain {{field}} lookups. */
export function generateTemplate(qs: SmartBlockQuerySpec): string {
  const from = qs.from;
  const fields = qs.fields && qs.fields.length ? qs.fields : defaultFieldsFor(from);
  const layout: LayoutKind = qs.layout ?? 'bulleted';
  const labels = qs.labels ?? {};

  if (layout === 'inline') {
    const body = fields.map((f) => `{{${f}}}`).join(' — ');
    return `{{#each items}}{{#if @index}}, {{/if}}${body}{{/each}}`;
  }
  if (layout === 'labeled') {
    const lines = fields.map((f) => `${labels[f] ?? labelForField(from, f)}: {{${f}}}`).join('\n');
    return `{{#each items}}\n${lines}\n{{/each}}`;
  }
  // bulleted (and the table fallback until the real table layout ships)
  const body = fields.map((f) => `{{${f}}}`).join(' — ');
  return `{{#each items}}\n${body}\n{{/each}}`;
}

/* ─── Sample project context — the live preview runs the REAL resolver
      against this, so what the author sees is what a project produces. ─── */

const NOW_ISO = new Date().toISOString();
const SAMPLE_CONTEXT: ProjectContext = {
  project: {
    id: 'p1',
    name: 'Riverside Medical Center',
    number: '23-114',
    address: '500 River Road, Austin, TX',
    phase: 'Construction Documents',
  },
  thisSpec: {
    id: 'this',
    csiNumber: '27 41 00',
    title: 'Audio-Visual Systems',
    division: '27',
    coreContent: [
      { id: 'c1', level: 2, text: 'Comply with ASTM E84 and UL 2043 for plenum-rated cable.' },
      { id: 'c2', level: 2, text: 'Conform to NFPA 70 and IEEE 802.3 requirements.' },
    ],
  },
  otherSpecsInProject: [
    {
      id: 's1',
      csiNumber: '27 11 16',
      title: 'Communications Equipment Room Fittings',
      division: '27',
    },
    { id: 's2', csiNumber: '27 13 23', title: 'Optical Fiber Backbone Cabling', division: '27' },
    { id: 's3', csiNumber: '26 05 19', title: 'Low-Voltage Conductors and Cables', division: '26' },
    { id: 's4', csiNumber: '28 31 00', title: 'Fire Detection and Alarm', division: '28' },
  ],
  team: [
    {
      id: 't1',
      name: 'Dana Reyes',
      role: 'pic',
      title: 'Principal in Charge',
      email: 'dreyes@firm.com',
    },
    {
      id: 't2',
      name: 'Marcus Lee',
      role: 'engineer',
      title: 'Electrical Engineer of Record',
      email: 'mlee@firm.com',
    },
    { id: 't3', name: 'Priya Shah', role: 'member', title: 'Designer', email: 'pshah@firm.com' },
  ],
  client: { id: 'cl1', name: 'Riverside Health System', address: '500 River Road, Austin, TX' },
  currentIssuance: {
    id: 'iss1',
    name: 'Issued for Construction',
    date: NOW_ISO,
    createdAt: NOW_ISO,
  },
  resolvedAt: NOW_ISO,
};

/* ─── Shared styles (inline tokens, matching the rest of the app) ──────── */

const card: React.CSSProperties = {
  background: 'var(--sf)',
  border: '1px solid var(--bd)',
  borderRadius: 10,
  padding: '14px 16px',
};
const sectionLabel: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: 0.6,
  textTransform: 'uppercase',
  color: 'var(--tm)',
  marginBottom: 12,
  display: 'flex',
  alignItems: 'center',
  gap: 6,
};
const fieldLabel: React.CSSProperties = {
  display: 'block',
  fontSize: 11,
  fontWeight: 600,
  color: 'var(--tm)',
  marginBottom: 4,
};
const input: React.CSSProperties = {
  width: '100%',
  padding: '7px 10px',
  fontSize: 12.5,
  border: '1px solid var(--bd)',
  borderRadius: 7,
  background: 'var(--sf)',
  color: 'var(--tx)',
  outline: 'none',
  boxSizing: 'border-box',
};

/* ═══ Rung tabs ════════════════════════════════════════════════════════ */

export function RungTabs({ rung, onChange }: { rung: RungId; onChange: (r: RungId) => void }) {
  const tabs: { id: RungId; label: string; sub: string; Icon: any }[] = [
    { id: 'quick', label: 'Quick', sub: 'Tweak a recipe', Icon: Wand2 },
    { id: 'build', label: 'Build', sub: 'Compose visually', Icon: SlidersHorizontal },
    { id: 'advanced', label: 'Advanced', sub: 'Query + template', Icon: Code2 },
  ];
  return (
    <div
      role="tablist"
      style={{
        display: 'inline-flex',
        gap: 4,
        padding: 4,
        background: 'var(--sa)',
        border: '1px solid var(--bd)',
        borderRadius: 10,
      }}
    >
      {tabs.map((t) => {
        const active = rung === t.id;
        return (
          <button
            key={t.id}
            role="tab"
            aria-selected={active}
            onClick={() => onChange(t.id)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '7px 13px',
              borderRadius: 7,
              border: 'none',
              cursor: 'pointer',
              background: active ? 'var(--sf)' : 'transparent',
              color: active ? 'var(--tx)' : 'var(--tm)',
              boxShadow: active ? '0 1px 3px rgba(0,0,0,0.10)' : 'none',
              transition: 'background 120ms ease, color 120ms ease',
            }}
          >
            <t.Icon size={14} strokeWidth={2.4} color={active ? 'var(--ac)' : 'var(--tm)'} />
            <span
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                lineHeight: 1.15,
              }}
            >
              <span style={{ fontSize: 12.5, fontWeight: 700 }}>{t.label}</span>
              <span style={{ fontSize: 9.5, fontWeight: 500, opacity: 0.8 }}>{t.sub}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

/* ═══ Live preview (real resolver, sample project) ═════════════════════ */

function PreviewTable({ td }: { td: TableData }) {
  const cells = td.cells ?? [];
  return (
    <div style={{ overflowX: 'auto', width: '100%' }}>
      <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12 }}>
        <tbody>
          {cells.map((row, ri) => {
            const isHeader = ri < (td.headerRows ?? 0);
            return (
              <tr key={ri}>
                {row.map((c, ci) => {
                  const Tag = (isHeader ? 'th' : 'td') as any;
                  return (
                    <Tag
                      key={ci}
                      style={{
                        border: '1px solid var(--bd)',
                        padding: '4px 8px',
                        textAlign: (c.align as any) ?? 'left',
                        fontWeight: isHeader || c.bold ? 700 : 400,
                        color: 'var(--tx)',
                        background: isHeader ? 'var(--sa)' : 'transparent',
                      }}
                    >
                      {c.text}
                    </Tag>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** Render a resolver's ContentBlock[] output — text lines + real tables.
 *  Shared by the builder preview and the project-spec resolved view. */
export function ResolvedBlocks({ blocks }: { blocks: ContentBlock[] }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {blocks.map((b, i) =>
        b.blockType === 'table' && b.tableData ? (
          <PreviewTable key={b.id || i} td={b.tableData} />
        ) : (
          <div
            key={b.id || i}
            style={{
              fontSize: 13,
              lineHeight: 1.5,
              color: 'var(--tx)',
              fontWeight: b.bold ? 700 : 400,
              fontStyle: b.italic ? 'italic' : 'normal',
              paddingLeft: (b.level ?? 0) * 14,
              whiteSpace: 'pre-wrap',
            }}
          >
            {b.text || ' '}
          </div>
        )
      )}
    </div>
  );
}

export function SmartBlockPreview({ draft }: { draft: SmartBlockDraft }) {
  const result = useMemo(() => {
    const kind = (draft.kind ?? 'custom') as SmartBlock['kind'];
    const synthetic = {
      id: draft.smartBlockId || 'preview',
      name: draft.name ?? 'Preview',
      kind,
      icon: draft.icon,
      color: draft.color,
      paramSchema: draft.paramSchema,
      defaultParams: draft.defaultParams,
      querySpec: draft.querySpec,
      templateSpec: draft.templateSpec,
      version: 0,
      lastModified: '',
    } as SmartBlock;
    const block = {
      id: 'preview-block',
      level: 0,
      text: '',
      blockType: 'smart-token',
      smartBlockId: draft.smartBlockId || (kind !== 'custom' ? builtInIdFor(kind) : ''),
      smartBlockKind: kind,
      smartParams: draft.defaultParams ?? {},
    } as ContentBlock;
    try {
      return {
        blocks: resolveSmartToken(block, synthetic, SAMPLE_CONTEXT),
        error: null as string | null,
      };
    } catch (e: any) {
      return { blocks: [] as ContentBlock[], error: e?.message ?? String(e) };
    }
  }, [
    draft.kind,
    draft.smartBlockId,
    draft.name,
    draft.icon,
    draft.color,
    JSON.stringify(draft.querySpec),
    draft.templateSpec,
    JSON.stringify(draft.defaultParams),
    JSON.stringify(draft.paramSchema),
  ]);

  const isCustom = (draft.kind ?? 'custom') === 'custom';
  const empty =
    !result.error &&
    (result.blocks.length === 0 ||
      result.blocks.every(
        (b) => b.blockType !== 'table' && b.blockType !== 'image' && !(b.text ?? '').trim()
      ));

  return (
    <div>
      <div style={{ ...sectionLabel, marginBottom: 8 }}>
        <Eye size={11} />
        Live preview
      </div>
      <div
        style={{
          fontSize: 10,
          color: 'var(--tm)',
          marginBottom: 10,
          lineHeight: 1.5,
        }}
      >
        Resolved against a sample project (Riverside Medical Center). A real project fills it with
        your actual data.
      </div>
      <div
        style={{
          background: 'var(--sf)',
          border: '1px solid var(--bd)',
          borderRadius: 8,
          padding: 14,
          minHeight: 120,
        }}
      >
        {result.error ? (
          <div style={{ fontSize: 12, color: 'var(--dg)', fontFamily: 'monospace' }}>
            {result.error}
          </div>
        ) : isCustom && !(draft.templateSpec ?? '').trim() && !draft.querySpec?.layout ? (
          <div style={{ fontSize: 12, fontStyle: 'italic', color: 'var(--tm)' }}>
            Choose a source and layout in <strong>Build</strong> to see the output here.
          </div>
        ) : empty ? (
          <div style={{ fontSize: 12, fontStyle: 'italic', color: 'var(--tm)' }}>
            Nothing to show for the sample project yet — adjust the filters or source.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {result.blocks.map((b, i) =>
              b.blockType === 'table' && b.tableData ? (
                <PreviewTable key={b.id || i} td={b.tableData} />
              ) : (
                <div
                  key={b.id || i}
                  style={{
                    fontSize: 13,
                    lineHeight: 1.5,
                    color: 'var(--tx)',
                    fontWeight: b.bold ? 700 : 400,
                    fontStyle: b.italic ? 'italic' : 'normal',
                    paddingLeft: (b.level ?? 0) * 14,
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {b.text || ' '}
                </div>
              )
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ═══ Icon picker ══════════════════════════════════════════════════════ */

function IconPicker({
  value,
  color,
  onChange,
}: {
  value?: string;
  color?: string;
  onChange: (n: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);
  const Current = ((LucideIcons as any)[value ?? 'Sparkles'] ?? LucideIcons.Sparkles) as any;
  const accent = color || 'var(--ac)';
  const filtered = q
    ? ICON_CHOICES.filter((n) => n.toLowerCase().includes(q.toLowerCase()))
    : ICON_CHOICES;
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        title="Choose an icon"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 38,
          height: 38,
          borderRadius: 9,
          border: '1px solid var(--bd)',
          background: 'var(--sf)',
          cursor: 'pointer',
        }}
      >
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 26,
            height: 26,
            borderRadius: 6,
            background: `${typeof accent === 'string' && accent.startsWith('#') ? accent + '1f' : 'rgba(var(--acr),0.12)'}`,
            color: accent,
          }}
        >
          <Current size={15} strokeWidth={2.4} />
        </span>
      </button>
      {open && (
        <div
          style={{
            position: 'absolute',
            top: 44,
            left: 0,
            zIndex: 100,
            width: 248,
            padding: 10,
            background: 'var(--sf)',
            border: '1px solid var(--bd)',
            borderRadius: 10,
            boxShadow: '0 8px 28px rgba(0,0,0,0.18)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '5px 8px',
              marginBottom: 8,
              background: 'var(--sa)',
              borderRadius: 6,
              border: '1px solid var(--bd)',
            }}
          >
            <Search size={12} color="var(--tm)" />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search icons…"
              style={{
                flex: 1,
                border: 'none',
                outline: 'none',
                background: 'transparent',
                fontSize: 12,
                color: 'var(--tx)',
              }}
            />
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(6, 1fr)',
              gap: 4,
              maxHeight: 200,
              overflowY: 'auto',
            }}
          >
            {filtered.map((n) => {
              const Ic = ((LucideIcons as any)[n] ?? LucideIcons.Sparkles) as any;
              const sel = n === value;
              return (
                <button
                  key={n}
                  type="button"
                  title={n}
                  onClick={() => {
                    onChange(n);
                    setOpen(false);
                  }}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: 32,
                    borderRadius: 6,
                    border: sel ? '1px solid var(--ac)' : '1px solid transparent',
                    background: sel ? 'rgba(var(--acr),0.12)' : 'transparent',
                    color: sel ? 'var(--ac)' : 'var(--tx)',
                    cursor: 'pointer',
                  }}
                >
                  <Ic size={15} strokeWidth={2.2} />
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══ Color swatches ═══════════════════════════════════════════════════ */

function ColorSwatches({ value, onChange }: { value?: string; onChange: (c: string) => void }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
      {COLOR_CHOICES.map((c) => {
        const sel = (value || '').toLowerCase() === c.toLowerCase();
        return (
          <button
            key={c}
            type="button"
            title={c}
            onClick={() => onChange(c)}
            style={{
              width: 24,
              height: 24,
              borderRadius: 6,
              background: c,
              border: sel ? '2px solid var(--tx)' : '2px solid transparent',
              boxShadow: sel ? '0 0 0 2px var(--sf)' : 'none',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {sel && <Check size={13} color="#fff" strokeWidth={3} />}
          </button>
        );
      })}
      <label
        title="Custom colour"
        style={{
          width: 24,
          height: 24,
          borderRadius: 6,
          border: '1px dashed var(--bd)',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <Plus size={13} color="var(--tm)" />
        <input
          type="color"
          value={value || '#1B8AAD'}
          onChange={(e) => onChange(e.target.value)}
          style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }}
        />
      </label>
    </div>
  );
}

/* ═══ Toggle ═══════════════════════════════════════════════════════════ */

function Toggle({
  on,
  onChange,
  label,
}: {
  on: boolean;
  onChange: (v: boolean) => void;
  label?: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      onClick={() => onChange(!on)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        border: 'none',
        background: 'transparent',
        cursor: 'pointer',
        padding: 0,
        color: 'var(--tx)',
        fontSize: 12.5,
      }}
    >
      <span
        style={{
          width: 34,
          height: 20,
          borderRadius: 999,
          background: on ? 'var(--ac)' : 'var(--bd)',
          position: 'relative',
          transition: 'background 140ms ease',
          flexShrink: 0,
        }}
      >
        <span
          style={{
            position: 'absolute',
            top: 2,
            left: on ? 16 : 2,
            width: 16,
            height: 16,
            borderRadius: '50%',
            background: '#fff',
            transition: 'left 140ms ease',
            boxShadow: '0 1px 2px rgba(0,0,0,0.25)',
          }}
        />
      </span>
      {label && <span>{label}</span>}
    </button>
  );
}

/* ═══ Friendly control for a single param's VALUE (used for defaults) ═══ */

function FriendlyParamControl({
  param,
  value,
  onChange,
}: {
  param: SmartBlockParam;
  value: any;
  onChange: (v: any) => void;
}) {
  switch (param.type) {
    case 'boolean':
      return <Toggle on={!!value} onChange={onChange} label={value ? 'Yes' : 'No'} />;
    case 'number':
      return (
        <input
          type="number"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value === '' ? undefined : Number(e.target.value))}
          placeholder={param.placeholder}
          style={input}
        />
      );
    case 'enum':
      return (
        <SearchableSelect
          value={value ?? ''}
          options={(param.options ?? []).map((o) => ({ value: o.value, label: o.label }))}
          onChange={onChange}
          searchPlaceholder="Search…"
          style={{ width: '100%' }}
        />
      );
    case 'multiselect': {
      const arr: string[] = Array.isArray(value) ? value : [];
      return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
          {(param.options ?? []).map((o) => {
            const on = arr.includes(o.value);
            return (
              <button
                key={o.value}
                type="button"
                onClick={() => onChange(on ? arr.filter((x) => x !== o.value) : [...arr, o.value])}
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  padding: '3px 9px',
                  borderRadius: 999,
                  cursor: 'pointer',
                  border: on ? '1px solid var(--ac)' : '1px solid var(--bd)',
                  background: on ? 'rgba(var(--acr),0.12)' : 'var(--sf)',
                  color: on ? 'var(--ac)' : 'var(--tm)',
                }}
              >
                {o.label}
              </button>
            );
          })}
        </div>
      );
    }
    case 'spec-ref-list':
      return (
        <div style={{ fontSize: 11.5, fontStyle: 'italic', color: 'var(--tm)' }}>
          Chosen per spec when the block is inserted.
        </div>
      );
    case 'division':
      return (
        <input
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={param.placeholder ?? 'e.g. 27'}
          style={{ ...input, maxWidth: 120 }}
        />
      );
    default:
      return (
        <input
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={param.placeholder}
          style={input}
        />
      );
  }
}

/* ═══ Parameter editor — declare the inputs a spec-author fills in ═════ */

function slugify(s: string): string {
  return (
    s
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '') || 'input'
  );
}

export function ParamSchemaEditor({
  params,
  onChange,
}: {
  params: SmartBlockParam[];
  onChange: (next: SmartBlockParam[]) => void;
}) {
  function patchAt(i: number, partial: Partial<SmartBlockParam>) {
    const next = params.slice();
    next[i] = { ...next[i], ...partial };
    onChange(next);
  }
  function add() {
    onChange([
      ...params,
      { key: `input_${params.length + 1}`, label: 'New input', type: 'string' },
    ]);
  }
  function remove(i: number) {
    onChange(params.filter((_, idx) => idx !== i));
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {params.length === 0 && (
        <div style={{ fontSize: 11.5, color: 'var(--tm)', fontStyle: 'italic' }}>
          No inputs yet. Inputs are the questions a spec writer answers when they drop this block
          into a section.
        </div>
      )}
      {params.map((p, i) => {
        const needsOptions = p.type === 'enum' || p.type === 'multiselect';
        return (
          <div key={i} style={{ ...card, padding: '10px 12px', background: 'var(--sa)' }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <input
                  value={p.label}
                  onChange={(e) => {
                    const label = e.target.value;
                    // keep key stable once edited away from the default slug
                    const autoKey =
                      !p.key || p.key.startsWith('input_') || p.key === slugify(p.label);
                    patchAt(i, autoKey ? { label, key: slugify(label) } : { label });
                  }}
                  placeholder="Question label, e.g. Division to list"
                  style={{ ...input, fontWeight: 600 }}
                />
              </div>
              <div style={{ width: 140, flexShrink: 0 }}>
                <SearchableSelect
                  value={p.type}
                  options={PARAM_TYPES.map((t) => ({ value: t.value, label: t.label }))}
                  onChange={(v) => patchAt(i, { type: v as SmartBlockParamType })}
                  searchPlaceholder="Type…"
                  clearable={false}
                  style={{ width: '100%' }}
                />
              </div>
              <button
                type="button"
                onClick={() => remove(i)}
                title="Remove input"
                style={{
                  flexShrink: 0,
                  width: 30,
                  height: 30,
                  borderRadius: 7,
                  border: '1px solid var(--bd)',
                  background: 'var(--sf)',
                  color: 'var(--dg)',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Trash2 size={13} />
              </button>
            </div>
            {needsOptions && (
              <div style={{ marginTop: 8 }}>
                <label style={fieldLabel}>Choices (comma-separated)</label>
                <input
                  value={(p.options ?? []).map((o) => o.label).join(', ')}
                  onChange={(e) =>
                    patchAt(i, {
                      options: e.target.value
                        .split(',')
                        .map((s) => s.trim())
                        .filter(Boolean)
                        .map((s) => ({ value: s, label: s })),
                    })
                  }
                  placeholder="e.g. ASTM, ANSI, UL"
                  style={input}
                />
              </div>
            )}
            <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 16 }}>
              <Toggle
                on={!!p.required}
                onChange={(v) => patchAt(i, { required: v })}
                label="Required"
              />
            </div>
          </div>
        );
      })}
      <button
        type="button"
        onClick={add}
        style={{
          alignSelf: 'flex-start',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '7px 12px',
          fontSize: 12,
          fontWeight: 600,
          borderRadius: 7,
          border: '1px dashed var(--bd)',
          background: 'var(--sf)',
          color: 'var(--ac)',
          cursor: 'pointer',
        }}
      >
        <Plus size={13} /> Add input
      </button>
    </div>
  );
}

/* ═══ QUICK rung — identity + default values, no logic ═════════════════ */

function QuickRung({ draft, update }: BodyProps) {
  const params = draft.paramSchema ?? [];
  function setDefault(key: string, val: any) {
    update('defaultParams', { ...(draft.defaultParams ?? {}), [key]: val });
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={card}>
        <div style={sectionLabel}>Identity</div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <div>
            <label style={fieldLabel}>Icon</label>
            <IconPicker
              value={draft.icon}
              color={draft.color}
              onChange={(n) => update('icon', n)}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={fieldLabel}>Description</label>
            <textarea
              value={draft.description ?? ''}
              onChange={(e) => update('description', e.target.value)}
              placeholder="What this block produces and when to use it…"
              rows={2}
              style={{ ...input, resize: 'vertical' }}
            />
          </div>
        </div>
        <div style={{ marginTop: 12, display: 'flex', gap: 24, flexWrap: 'wrap' }}>
          <div>
            <label style={fieldLabel}>Accent colour</label>
            <ColorSwatches value={draft.color} onChange={(c) => update('color', c)} />
          </div>
          <div style={{ minWidth: 160 }}>
            <label style={fieldLabel}>Category</label>
            <input
              value={draft.category ?? ''}
              onChange={(e) => update('category', e.target.value)}
              placeholder="e.g. Cross-reference"
              style={input}
            />
          </div>
        </div>
      </div>

      <div style={card}>
        <div style={sectionLabel}>Default values</div>
        {params.length === 0 ? (
          <div style={{ fontSize: 12, color: 'var(--tm)', fontStyle: 'italic', lineHeight: 1.5 }}>
            This block has no options to preset. Switch to <strong>Build</strong> to give it a
            source and inputs, or <strong>Advanced</strong> for full control.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {params.map((p) => (
              <div key={p.key}>
                <label style={fieldLabel}>
                  {p.label}
                  {p.description && (
                    <span style={{ fontWeight: 400, color: 'var(--tm)' }}> — {p.description}</span>
                  )}
                </label>
                <FriendlyParamControl
                  param={p}
                  value={(draft.defaultParams ?? {})[p.key]}
                  onChange={(v) => setDefault(p.key, v)}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ═══ BUILD rung — the visual no-code composer ════════════════════════ */

function BuildComposer({ draft, patch }: BodyProps) {
  const qs: SmartBlockQuerySpec = draft.querySpec ?? { from: 'project-specs' };
  const from = qs.from;
  const fields = qs.fields && qs.fields.length ? qs.fields : defaultFieldsFor(from);
  const layout: LayoutKind = qs.layout ?? 'bulleted';
  const clauses = qs.where ?? [];
  const sourceFields = FIELDS_BY_SOURCE[from];
  const params = draft.paramSchema ?? [];

  // Commit a new querySpec: stamp friendly field labels (used by the
  // 'labeled' + 'table' layouts), regenerate the template, optionally swap
  // params — all in one atomic update.
  function commit(nextQs: SmartBlockQuerySpec, nextParams?: SmartBlockParam[]) {
    const cFields =
      nextQs.fields && nextQs.fields.length ? nextQs.fields : defaultFieldsFor(nextQs.from);
    const withLabels: SmartBlockQuerySpec = {
      ...nextQs,
      labels: Object.fromEntries(cFields.map((f) => [f, labelForField(nextQs.from, f)])),
    };
    patch({
      querySpec: withLabels,
      templateSpec: generateTemplate(withLabels),
      ...(nextParams ? { paramSchema: nextParams } : {}),
    });
  }

  function setSource(next: QuerySource) {
    // changing source invalidates field/filter references → reset to defaults
    commit({ from: next, layout, fields: defaultFieldsFor(next) });
  }
  function setLayout(next: LayoutKind) {
    commit({ ...qs, layout: next });
  }
  function toggleField(f: string) {
    const on = fields.includes(f);
    const next = on ? fields.filter((x) => x !== f) : [...fields, f];
    commit({ ...qs, fields: next.length ? next : [f] });
  }

  // ── filter rows ──
  function addFilter() {
    commit({
      ...qs,
      where: [...clauses, { field: sourceFields[0]?.value ?? '', op: 'equals', value: '' }],
    });
  }
  function patchFilter(i: number, partial: Partial<SmartBlockQueryClause>) {
    const next = clauses.slice();
    next[i] = { ...next[i], ...partial };
    commit({ ...qs, where: next });
  }
  function removeFilter(i: number) {
    const removed = clauses[i];
    const nextClauses = clauses.filter((_, idx) => idx !== i);
    // If this row asked-each-time, drop its generated param too.
    let nextParams = params;
    if (typeof removed?.value === 'string' && removed.value.startsWith('$')) {
      const key = removed.value.slice(1);
      nextParams = params.filter((p) => p.key !== key);
    }
    commit({ ...qs, where: nextClauses }, nextParams);
  }
  function setAskEachTime(i: number, ask: boolean) {
    const clause = clauses[i];
    const nextClauses = clauses.slice();
    let nextParams = params.slice();
    if (ask) {
      const key = `ask_${slugify(clause.field || 'value')}`;
      const label = `${labelForField(from, clause.field)} to match`;
      if (!nextParams.some((p) => p.key === key)) {
        nextParams.push({
          key,
          label,
          type: clause.field === 'division' ? 'division' : 'string',
        });
      }
      nextClauses[i] = { ...clause, value: `$${key}` };
    } else {
      if (typeof clause.value === 'string' && clause.value.startsWith('$')) {
        const key = clause.value.slice(1);
        nextParams = nextParams.filter((p) => p.key !== key);
      }
      nextClauses[i] = { ...clause, value: '' };
    }
    commit({ ...qs, where: nextClauses }, nextParams);
  }

  const singleRecord = from === 'project-fields' || from === 'issuances';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Source */}
      <div style={card}>
        <div style={sectionLabel}>1 · What to pull in</div>
        <SearchableSelect
          value={from}
          options={SOURCES.map((s) => ({ value: s.value, label: s.label }))}
          onChange={(v) => setSource(v as QuerySource)}
          searchPlaceholder="Search sources…"
          clearable={false}
          style={{ width: '100%' }}
        />
        <div style={{ fontSize: 11, color: 'var(--tm)', marginTop: 6 }}>
          {SOURCES.find((s) => s.value === from)?.hint}
        </div>
      </div>

      {/* Filters */}
      <div style={card}>
        <div style={sectionLabel}>2 · Narrow it down</div>
        {clauses.length === 0 && (
          <div style={{ fontSize: 12, color: 'var(--tm)', fontStyle: 'italic', marginBottom: 10 }}>
            No filters — every item is included.
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {clauses.map((c, i) => {
            const asking = typeof c.value === 'string' && c.value.startsWith('$');
            const askKey = asking ? (c.value as string).slice(1) : '';
            const askParam = params.find((p) => p.key === askKey);
            return (
              <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <div style={{ width: 150, flexShrink: 0 }}>
                    <SearchableSelect
                      value={c.field}
                      options={sourceFields.map((f) => ({ value: f.value, label: f.label }))}
                      onChange={(v) => patchFilter(i, { field: v })}
                      searchPlaceholder="Field…"
                      clearable={false}
                      style={{ width: '100%' }}
                    />
                  </div>
                  <div style={{ width: 118, flexShrink: 0 }}>
                    <SearchableSelect
                      value={c.op}
                      options={OPERATORS.map((o) => ({ value: o.value, label: o.label }))}
                      onChange={(v) => patchFilter(i, { op: v as SmartBlockQueryClause['op'] })}
                      searchPlaceholder=""
                      clearable={false}
                      style={{ width: '100%' }}
                    />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {asking ? (
                      <div
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          padding: '7px 10px',
                          borderRadius: 7,
                          border: '1px dashed var(--ac)',
                          background: 'rgba(var(--acr),0.08)',
                          color: 'var(--ac)',
                          fontSize: 11.5,
                          fontWeight: 600,
                          width: '100%',
                          boxSizing: 'border-box',
                        }}
                      >
                        <CornerDownRight size={12} />
                        Asked when inserted
                      </div>
                    ) : (
                      <input
                        value={typeof c.value === 'string' ? c.value : ''}
                        onChange={(e) => patchFilter(i, { value: e.target.value })}
                        placeholder="value"
                        style={input}
                      />
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => removeFilter(i)}
                    title="Remove filter"
                    style={{
                      flexShrink: 0,
                      width: 30,
                      height: 30,
                      borderRadius: 7,
                      border: '1px solid var(--bd)',
                      background: 'var(--sf)',
                      color: 'var(--dg)',
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <X size={13} />
                  </button>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingLeft: 2 }}>
                  <Toggle on={asking} onChange={(v) => setAskEachTime(i, v)} />
                  <span style={{ fontSize: 11, color: 'var(--tm)' }}>
                    Ask me each time (turns this into an input the writer fills in)
                  </span>
                  {asking && askParam && (
                    <input
                      value={askParam.label}
                      onChange={(e) => {
                        const next = params.map((p) =>
                          p.key === askKey ? { ...p, label: e.target.value } : p
                        );
                        patch({ paramSchema: next });
                      }}
                      placeholder="Prompt label"
                      style={{ ...input, maxWidth: 220, padding: '4px 8px', fontSize: 11.5 }}
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <button
          type="button"
          onClick={addFilter}
          style={{
            marginTop: 10,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '6px 11px',
            fontSize: 12,
            fontWeight: 600,
            borderRadius: 7,
            border: '1px dashed var(--bd)',
            background: 'var(--sf)',
            color: 'var(--ac)',
            cursor: 'pointer',
          }}
        >
          <Plus size={13} /> Add filter
        </button>
      </div>

      {/* Order + limit */}
      {!singleRecord && (
        <div style={card}>
          <div style={sectionLabel}>3 · Order &amp; limit</div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ minWidth: 170 }}>
              <label style={fieldLabel}>Sort by</label>
              <SearchableSelect
                value={qs.sort ?? ''}
                options={[
                  { value: '', label: 'Don’t sort' },
                  ...sourceFields.map((f) => ({ value: f.value, label: f.label })),
                ]}
                onChange={(v) => commit({ ...qs, sort: v || undefined })}
                searchPlaceholder="Field…"
                style={{ width: '100%' }}
              />
            </div>
            <div style={{ minWidth: 120 }}>
              <label style={fieldLabel}>Direction</label>
              <SearchableSelect
                value={qs.sortDir ?? 'asc'}
                options={[
                  { value: 'asc', label: 'A → Z' },
                  { value: 'desc', label: 'Z → A' },
                ]}
                onChange={(v) => commit({ ...qs, sortDir: v as 'asc' | 'desc' })}
                searchPlaceholder=""
                clearable={false}
                style={{ width: '100%' }}
              />
            </div>
            <div style={{ width: 110 }}>
              <label style={fieldLabel}>Show at most</label>
              <input
                type="number"
                min={1}
                value={qs.limit ?? ''}
                onChange={(e) =>
                  commit({
                    ...qs,
                    limit: e.target.value === '' ? undefined : Number(e.target.value),
                  })
                }
                placeholder="All"
                style={input}
              />
            </div>
          </div>
        </div>
      )}

      {/* Layout */}
      <div style={card}>
        <div style={sectionLabel}>{singleRecord ? '3' : '4'} · How it looks</div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: 8,
          }}
        >
          {LAYOUTS.map((l) => {
            const sel = layout === l.value;
            return (
              <button
                key={l.value}
                type="button"
                disabled={l.soon}
                onClick={() => !l.soon && setLayout(l.value)}
                style={{
                  textAlign: 'left',
                  padding: '10px 12px',
                  borderRadius: 9,
                  border: sel ? '1px solid var(--ac)' : '1px solid var(--bd)',
                  background: sel ? 'rgba(var(--acr),0.08)' : 'var(--sf)',
                  cursor: l.soon ? 'not-allowed' : 'pointer',
                  opacity: l.soon ? 0.55 : 1,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span
                    style={{
                      fontSize: 12.5,
                      fontWeight: 700,
                      color: sel ? 'var(--ac)' : 'var(--tx)',
                    }}
                  >
                    {l.label}
                  </span>
                  {l.soon && (
                    <span
                      style={{
                        fontSize: 8.5,
                        fontWeight: 700,
                        letterSpacing: 0.5,
                        textTransform: 'uppercase',
                        color: 'var(--tm)',
                        border: '1px solid var(--bd)',
                        borderRadius: 4,
                        padding: '1px 4px',
                      }}
                    >
                      Soon
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 10.5, color: 'var(--tm)', marginTop: 2 }}>{l.hint}</div>
              </button>
            );
          })}
        </div>

        {/* Fields to show */}
        <div style={{ marginTop: 14 }}>
          <label style={fieldLabel}>Show these fields</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {sourceFields.map((f) => {
              const on = fields.includes(f.value);
              return (
                <button
                  key={f.value}
                  type="button"
                  onClick={() => toggleField(f.value)}
                  style={{
                    fontSize: 11.5,
                    fontWeight: 600,
                    padding: '4px 10px',
                    borderRadius: 999,
                    cursor: 'pointer',
                    border: on ? '1px solid var(--ac)' : '1px solid var(--bd)',
                    background: on ? 'rgba(var(--acr),0.12)' : 'var(--sf)',
                    color: on ? 'var(--ac)' : 'var(--tm)',
                  }}
                >
                  {f.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Inputs */}
      <div style={card}>
        <div style={sectionLabel}>{singleRecord ? '4' : '5'} · Inputs the writer fills in</div>
        <ParamSchemaEditor params={params} onChange={(next) => patch({ paramSchema: next })} />
      </div>
    </div>
  );
}

/* ═══ ADVANCED rung — raw query + template, cleaned up ════════════════ */

function AdvancedRung({ draft, update, patch }: BodyProps) {
  const qs: SmartBlockQuerySpec = draft.querySpec ?? { from: 'project-specs' };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={card}>
        <div style={sectionLabel}>Identity</div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <div>
            <label style={fieldLabel}>Icon</label>
            <IconPicker
              value={draft.icon}
              color={draft.color}
              onChange={(n) => update('icon', n)}
            />
          </div>
          <div>
            <label style={fieldLabel}>Accent colour</label>
            <ColorSwatches value={draft.color} onChange={(c) => update('color', c)} />
          </div>
          <div style={{ minWidth: 160 }}>
            <label style={fieldLabel}>Category</label>
            <input
              value={draft.category ?? ''}
              onChange={(e) => update('category', e.target.value)}
              placeholder="Custom"
              style={input}
            />
          </div>
        </div>
      </div>

      <div style={card}>
        <div style={sectionLabel}>Source query</div>
        <label style={fieldLabel}>Source</label>
        <SearchableSelect
          value={qs.from}
          options={SOURCES.map((s) => ({ value: s.value, label: s.label }))}
          onChange={(v) => update('querySpec', { ...qs, from: v as QuerySource })}
          searchPlaceholder="Source…"
          clearable={false}
          style={{ width: '100%' }}
        />
        <div style={{ display: 'flex', gap: 12, marginTop: 12, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 150 }}>
            <label style={fieldLabel}>Sort by (raw field name)</label>
            <input
              value={qs.sort ?? ''}
              onChange={(e) => update('querySpec', { ...qs, sort: e.target.value || undefined })}
              placeholder="e.g. csiNumber"
              style={{ ...input, fontFamily: 'monospace', fontSize: 11.5 }}
            />
          </div>
          <div style={{ width: 110 }}>
            <label style={fieldLabel}>Limit</label>
            <input
              type="number"
              value={qs.limit ?? ''}
              onChange={(e) =>
                update('querySpec', {
                  ...qs,
                  limit: e.target.value === '' ? undefined : Number(e.target.value),
                })
              }
              placeholder="None"
              style={input}
            />
          </div>
        </div>
      </div>

      <div style={card}>
        <div style={sectionLabel}>Template (Handlebars-lite)</div>
        <textarea
          value={draft.templateSpec ?? ''}
          onChange={(e) => update('templateSpec', e.target.value)}
          rows={9}
          placeholder={'{{#each items}}\n{{csiNumber}} — {{title}}\n{{/each}}'}
          style={{
            ...input,
            fontFamily: 'monospace',
            fontSize: 11.5,
            lineHeight: 1.55,
            resize: 'vertical',
          }}
        />
        <div style={{ fontSize: 10, color: 'var(--tm)', lineHeight: 1.5, marginTop: 6 }}>
          Supports {'{{field}}'}, {'{{#each items}}…{{/each}}'}, {'{{#if x}}…{{/if}}'},{' '}
          {'{{join items "sep"}}'}, {'{{upper}}'}, {'{{lower}}'}, {'{{count}}'}. Each line of output
          becomes its own paragraph.
        </div>
      </div>

      <div style={card}>
        <div style={sectionLabel}>Inputs</div>
        <ParamSchemaEditor
          params={draft.paramSchema ?? []}
          onChange={(next) => patch({ paramSchema: next })}
        />
      </div>
    </div>
  );
}

/* ═══ Dispatcher + helpers ════════════════════════════════════════════ */

export function SmartBlockBuilderBody({ rung, ...rest }: BodyProps & { rung: RungId }) {
  if (rung === 'quick') return <QuickRung {...rest} />;
  if (rung === 'advanced') return <AdvancedRung {...rest} />;
  return <BuildComposer {...rest} />;
}

/** Which rung to open a draft in. Built-in presets → Quick (tweak defaults);
 *  visually-composed customs → Build; hand-authored customs → Advanced;
 *  brand-new empty customs → Build. */
export function initialRungFor(draft: SmartBlockDraft): RungId {
  const kind = draft.kind ?? 'custom';
  if (kind !== 'custom') return 'quick';
  if (draft.querySpec?.layout) return 'build';
  if ((draft.templateSpec ?? '').trim()) return 'advanced';
  return 'build';
}
